import React from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';

const KeyRequestDialog = ({
  open,
  onClose,
  selectedDeposit,
  sourceChain,
  supportedChains,
  denominationRequests,
  availableTokens,
  keyRequestLoading,
  onUpdateRequest,
  onAddRequest,
  onRemoveRequest,
  onExecute,
  isMobile,
}) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={isMobile}>
      <DialogTitle>Request Cross-Chain Withdrawal Keys</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Request keys for your deposit of {selectedDeposit?.amount} {selectedDeposit?.metadata?.token || 'ETH'}.
          You can now request keys for different chains in a single request!
        </Typography>

        <FormControl fullWidth sx={{ mb: 3 }} disabled>
          <InputLabel>Source Chain (Where Deposit Was Made)</InputLabel>
          <Select
            value={sourceChain}
            label="Source Chain (Where Deposit Was Made)"
            disabled
          >
            {supportedChains.map(chain => (
              <MenuItem key={chain.chain_name} value={chain.chain_name}>
                {chain.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Source Chain:</strong> Automatically set to <strong>{supportedChains.find(c => c.chain_name === sourceChain)?.name || sourceChain}</strong> based on your deposit transaction.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Token:</strong> Using <strong>{selectedDeposit?.metadata?.token || 'ETH'}</strong> from your deposit.
          </Typography>
        </Alert>

        <Typography variant="h6" sx={{ mb: 2 }}>
          Denomination Requests
        </Typography>

        {denominationRequests.map((request, index) => (
          <Box key={index} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1.5, sm: 2 }, mb: 2, alignItems: 'flex-start' }}>
            <TextField
              label={`Denomination ${index + 1}`}
              value={request.denomination}
              onChange={(e) => onUpdateRequest(index, 'denomination', e.target.value)}
              placeholder="1.0"
              type="number"
              inputProps={{ step: '0.01', min: '0' }}
              sx={{ flex: { xs: '1 1 100%', sm: '0 0 120px' }, width: { xs: '100%', sm: 'auto' } }}
            />
            <FormControl sx={{ flex: { xs: '1 1 100%', sm: '0 0 140px' }, width: { xs: '100%', sm: 'auto' } }}>
              <InputLabel>Token</InputLabel>
              <Select
                value={request.token}
                onChange={(e) => onUpdateRequest(index, 'token', e.target.value)}
                label="Token"
              >
                {availableTokens.length > 0 ? (
                  availableTokens.map(token => (
                    <MenuItem key={token.symbol} value={token.symbol}>
                      {token.symbol}
                    </MenuItem>
                  ))
                ) : (
                  // Fallback to default tokens if none loaded
                  <>
                    <MenuItem value="ETH">ETH</MenuItem>
                    <MenuItem value="SOL">SOL</MenuItem>
                  </>
                )}
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel>Target Chain</InputLabel>
              <Select
                value={request.chain}
                onChange={(e) => onUpdateRequest(index, 'chain', e.target.value)}
                label="Target Chain"
              >
                {supportedChains.map(chain => (
                  <MenuItem key={chain.chain_name} value={chain.chain_name}>
                    {chain.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {denominationRequests.length > 1 && (
                <IconButton
                  onClick={() => onRemoveRequest(index)}
                  color="error"
                  size="small"
                >
                  <Remove />
                </IconButton>
              )}
              {index === denominationRequests.length - 1 && (
                <IconButton
                  onClick={onAddRequest}
                  color="primary"
                  size="small"
                >
                  <Add />
                </IconButton>
              )}
            </Box>
          </Box>
        ))}

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Supported denominations:</strong> 0.001, 0.01, 0.1, 0.5, 1.0 per token
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Mixed cross-chain withdrawals:</strong> You can request different tokens on different chains! For example:
          </Typography>
          <Typography variant="body2" component="div" sx={{ mt: 0.5, ml: 2 }}>
            {'\u2022'} 1.0 ETH for Arbitrum<br />
            {'\u2022'} 1.0 WETH for Polygon<br />
            {'\u2022'} Backend validates token mappings and ensures security
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Security:</strong> The backend verifies that your deposit token can be mapped to the requested withdrawal tokens on the target chains.
          </Typography>
        </Alert>

        {keyRequestLoading && (
          <Alert severity="info" sx={{ mt: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              Requesting keys from node...
            </Box>
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={keyRequestLoading}>
          Cancel
        </Button>
        <Button
          onClick={onExecute}
          variant="contained"
          disabled={keyRequestLoading}
          startIcon={keyRequestLoading ? <CircularProgress size={16} /> : undefined}
        >
          {keyRequestLoading ? 'Requesting...' : 'Request Keys'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KeyRequestDialog;
